import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { DocumentApiProvider, useDocumentApi } from '../Services/DocumentApiProvider';
import { formatDateUTC } from '../utils/formatDate';
import { getTablePaginationFromStorage } from '../utils/tablePaginationStorage';
import { formatTableTitle } from '../utils/formatTitle';

function DocumentsHub({ canEdit }) {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const { getDocuments, deleteDocument } = useDocumentApi();

  const [alertShown, setAlertShown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [totalDocuments, setTotalDocuments] = useState(0);

  const tableTitle = 'Documents';
  const { persistedPageSize, persistedSkip } = getTablePaginationFromStorage(
    formatTableTitle(tableTitle),
  );

  useEffect(() => {
    if (location.state?.successMessage && !alertShown) {
      showAlert('success', location.state.successMessage);
      setAlertShown(true);

      navigate(location.pathname, { replace: true, state: {} });
    }
    if (location.state?.returnMessage && !alertShown) {
      showAlert('help', location.state.returnMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.successMessage, alertShown, navigate, location.pathname, showAlert]);

  // function to delete document
  const handleDelete = async (selectedIds) => {
    if (!selectedIds || selectedIds.length === 0) return;
    console.log('Deleting IDs:', selectedIds);

    showConfirmation(`Are you sure you want to delete ${selectedIds.length} item(s)?`, async () => {
      try {
        for (const id of selectedIds) {
          await deleteDocument(id);
        }
        showAlert('success', 'Items were successfully deleted!');
        fetchDocuments(persistedSkip, persistedPageSize);
      } catch (error) {
        showAlert('fail', 'Failed to delete the items!');
      }
    });
  };

  const fetchDocuments = async (skip, limit) => {
    try {
      setLoading(true);
      const response = await getDocuments(skip, limit);
      setDocuments(response.items);
      setTotalDocuments(response.totalCount);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setError(error.message || 'Failed to fetch documents!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments(persistedSkip, persistedPageSize);
  }, []);

  const columns = [
    { name: 'ID', selector: (row) => row.id, sortable: false, width: '200px' },
    { name: 'Name', selector: (row) => row.original_name, sortable: false, width: '350px' },
    { name: 'Type', selector: (row) => row.type, sortable: false, width: '120px' },
    {
      name: 'Tags',
      selector: (row) => row.tags.join(', '),
      sortable: true,
      width: '300px',
      wrap: false,
    },
    {
      name: 'Source Type',
      selector: (row) => row.source_type,
      sortable: true,
      width: '200px',
      wrap: false,
    },

    {
      name: 'Created',
      selector: (row) => formatDateUTC(row.created_at),
      sortable: false,
      width: '180px',
    },
    {
      name: 'Updated',
      selector: (row) => formatDateUTC(row.updated_at),
      sortable: false,
      width: '180px',
    },
  ];

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <p>Error loading documents: {error}</p>
          ) : (
            <>
              <Table
                title={tableTitle}
                columns={columns}
                data={documents}
                selectableRows
                selectableRowsHighlight
                progressPending={loading}
                paginationPerPage={persistedPageSize}
                paginationTotalRows={totalDocuments}
                dense={false}
                noTableHead={false}
                disabled={false}
                onRowClicked={(row) =>
                  navigate(`/organizations/${organizationId}/documents-hub/${row.id}`)
                }
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onDelete={handleDelete}
                singleSelect={false}
                onChangePage={fetchDocuments}
                getRowUrl={(row) => `/organizations/${organizationId}/documents-hub/${row.id}`}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() =>
                    navigate(`/organizations/${organizationId}/documents-hub/new-document`)
                  }
                >
                  Create New document
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DocumentsHubWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <DocumentApiProvider>
        <DocumentsHub canEdit={canEdit} />
      </DocumentApiProvider>
    </ConfirmationProvider>
  );
}

export default DocumentsHubWrapper;
